import CloseIcon from '@mui/icons-material/Close';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import {Box, Divider, IconButton, Tooltip} from '@mui/material';
import {
  ComponentProps,
  JSXElementConstructor,
  ReactNode,
  useState,
} from 'react';

import {ExportField} from '../../interfaces/Export';
import ExportPdfButton from '../common/ExportPdfButton';
import ModalFixed from '../common/ModalFixed';
import CommtracNodeTrackingExportExcel from '../dashboard-panels/CommtracNodeTrackingReports/CommtracNodeTrackingExportExcel';
import {CommtracNodesReportData} from '../dashboard-panels/ConnectView/CommtracNodesReport';

type ComponentType = keyof JSX.IntrinsicElements | JSXElementConstructor<any>;

interface Props<T extends ComponentType> {
  component?: T;
  componentProps?: ComponentProps<T>;
  children?: ReactNode;
  value?: CommtracNodesReportData;
  onSubmitted?: () => void;
  exportType: 'asset' | 'employee';
  exportFields?: ExportField[];
  params?: Record<string, unknown>;
}

const exportFieldsAsset = [
  {field: 'battery_voltage', hidden: false, label: 'Battery'},
  {field: 'timestamp', hidden: false, label: 'Timestamp'},
  {field: 'message_type', hidden: false, label: 'Message Type'},
  {
    field: 'commtrac_current_zone_name',
    hidden: false,
    label: 'Section',
  },
  {field: 'strongest_cn_name', hidden: false, label: 'Strongest Node'},
  {field: 'longitude', hidden: false, label: 'Longitude'},
  {field: 'latitude', hidden: false, label: 'Latitude'},
  {field: 'beacon_data', hidden: false, label: 'Beacon Data'},
];

const exportFieldsEmployee = [
  {field: 'battery_voltage', hidden: false, label: 'Battery'},
  {field: 'timestamp', hidden: false, label: 'Timestamp'},
  {field: 'message_type', hidden: false, label: 'Type'},
  {field: 'motion_sensor_value', hidden: false, label: 'Motion State'},
  {field: 'message', hidden: false, label: 'Message'},
  {field: 'message_id', hidden: false, label: 'Message ID'},
  {field: 'status', hidden: false, label: 'Status'},
  {
    field: 'commtrac_current_zone_name',
    hidden: false,
    label: 'Section',
  },
  {field: 'strongest_cn_name', hidden: false, label: 'Strongest Node'},
  {field: 'longitude', hidden: false, label: 'Longitude'},
  {field: 'latitude', hidden: false, label: 'Latitude'},
  {field: 'beacon_data', hidden: false, label: 'Beacon Data'},
];

const CommtracNodeHistoryExportButton = <T extends ComponentType>({
  componentProps,
  children,
  exportType,
  exportFields,
  params,
}: Props<T>) => {
  const fieldsToExport =
    exportFields ??
    (exportType === 'asset' ? exportFieldsAsset : exportFieldsEmployee);
  const [isOpened, setIsOpened] = useState(false);

  return (
    <>
      <Tooltip title="Export">
        <FileDownloadIcon {...componentProps} onClick={() => setIsOpened(true)}>
          {children}
        </FileDownloadIcon>
      </Tooltip>

      {isOpened ? (
        <ModalFixed open={isOpened} onClose={() => setIsOpened(false)}>
          <Box
            display="flex"
            flexDirection="column"
            position="relative"
            gap={3}
            p={3}
          >
            <Box display="flex" justifyContent="space-between">
              <Box display="flex" alignItems="center" width="100%" gap={1.5}>
                <FileDownloadIcon sx={{color: 'primary.main'}} />
                <Box fontSize={24}>Export History</Box>
              </Box>
              <IconButton onClick={() => setIsOpened(false)}>
                <CloseIcon />
              </IconButton>
            </Box>

            <Box
              display="flex"
              flexDirection="column"
              position="relative"
              gap={3}
            >
              <Divider>Export</Divider>
              <CommtracNodeTrackingExportExcel
                value={{
                  ...componentProps,
                  exportFields: fieldsToExport,
                  shownFields: [],
                  selectedIds: null,
                  params: {...params, id: componentProps?.id},
                }}
              />
              <ExportPdfButton
                endpoint={`/commtrac-node/${componentProps?.id}/history`}
                defaultFilename={`${exportType}_tracking_history.pdf`}
                params={params}
                exportFields={fieldsToExport}
              />
            </Box>
          </Box>
        </ModalFixed>
      ) : null}
    </>
  );
};

export default CommtracNodeHistoryExportButton;
